import {
	TrackingBuyerStep,
	TrackingDocumentGroupKey,
	TrackingDriverNoticeKey,
	TrackingStepKey,
	TrackingTab,
} from './orderTracking.interface';

export const TRACKING_STEPS: {
	key: TrackingStepKey;
	labelKey: string;
	defaultLabel: string;
}[] = [
	{
		key: 'accepted',
		labelKey: 'orderTracking.steps.accepted',
		defaultLabel: 'Accepted the load',
	},
	{
		key: 'to_loading',
		labelKey: 'orderTracking.steps.toLoading',
		defaultLabel: 'En route to loading',
	},
	{
		key: 'arrived_to_loading',
		labelKey: 'orderTracking.steps.arrivedToLoading',
		defaultLabel: 'Arrived at loading site',
	},
	{
		key: 'loaded',
		labelKey: 'orderTracking.steps.loaded',
		defaultLabel: 'Loaded',
	},
	{
		key: 'to_unloading',
		labelKey: 'orderTracking.steps.toUnloading',
		defaultLabel: 'En route to unloading',
	},
	{
		key: 'unloaded',
		labelKey: 'orderTracking.steps.unloaded',
		defaultLabel: 'Unloaded',
	},
	{
		key: 'completed',
		labelKey: 'orderTracking.steps.orderCompleted',
		defaultLabel: 'Order completed',
	},
];

export const TRACKING_DRIVER_NOTICES: Record<
	TrackingDriverNoticeKey,
	{ labelKey: string; defaultLabel: string }
> = {
	noDriver: {
		labelKey: 'orderTracking.driverNotice.noDriver',
		defaultLabel:
			'No driver has been assigned yet. The truck will appear on the map once a driver takes the order.',
	},
	notStarted: {
		labelKey: 'orderTracking.driverNotice.notStarted',
		defaultLabel:
			'The driver has not started the trip yet. The truck will appear on the map once it is on its way.',
	},
	awaitingPickup: {
		labelKey: 'orderTracking.driverNotice.awaitingPickup',
		defaultLabel:
			'The truck will appear on the map once it has been loaded and leaves the pick-up site.',
	},
	leftPickup: {
		labelKey: 'orderTracking.driverNotice.leftPickup',
		defaultLabel:
			'The truck has left the pick-up site. Its trip to the buyer is not shown here.',
	},
	waitingForDriver: {
		labelKey: 'orderTracking.driverNotice.waitingForDriver',
		defaultLabel:
			'There is no information from the driver yet. We are waiting for their first location.',
	},
	liveDataPaused: {
		labelKey: 'orderTracking.driverNotice.liveDataPaused',
		defaultLabel:
			'Live distance and arrival time are paused until the driver app reconnects.',
	},
};

export const TRACKING_ROLE_LABELS: Record<
	string,
	{ labelKey: string; defaultLabel: string }
> = {
	driver: { labelKey: 'orderTracking.roles.driver', defaultLabel: 'Driver' },
	dispatcher: {
		labelKey: 'orderTracking.roles.dispatcher',
		defaultLabel: 'Dispatcher',
	},
	admin: { labelKey: 'orderTracking.roles.admin', defaultLabel: 'Admin' },
	producer: {
		labelKey: 'orderTracking.roles.producer',
		defaultLabel: 'Seller',
	},
	customer: {
		labelKey: 'orderTracking.roles.customer',
		defaultLabel: 'Buyer',
	},
};

// track.sypac.app refreshes the order (route, truck, status) once a minute.
export const TRACKING_POLL_INTERVAL_MS = 60000;

export const TRACKING_LIVE_FIX_MAX_AGE_MS = 10 * 60 * 1000;

// How a truck marker moves between two live fixes (see truckMotion.controller).
// `enabled: false` puts markers straight on every new fix, as before.
export const TRACKING_TRUCK_MOTION = {
	enabled: true,
	defaultDurationMs: 2000,
	minDurationMs: 500,
	maxDurationMs: 4000,
	teleportDistanceM: 500,
	teleportGapMs: 30000,
};

// Below this the speed says nothing about the arrival (a jam, a long stop).
export const TRACKING_ETA_MIN_SPEED_KMH = 5;

export const TRACKING_MAP_PADDING = {
	all: {
		topLeft: [60, 160] as [number, number],
		bottomRight: [60, 110] as [number, number],
	},
	selected: {
		topLeft: [80, 270] as [number, number],
		bottomRight: [80, 80] as [number, number],
	},
	compact: {
		topLeft: [24, 90] as [number, number],
		bottomRight: [24, 24] as [number, number],
	},
	compactSelected: {
		topLeft: [24, 200] as [number, number],
		bottomRight: [24, 24] as [number, number],
	},
};

export const TRACKING_DRAWER_TRANSITION = {
	duration: 0.32,
	ease: [0.2, 0, 0.2, 1] as [number, number, number, number],
};

export const TRACKING_STATUS_COLOR_CLASSES: Record<string, string> = {
	red: 'bg-red-10 text-red',
	yellow: 'bg-track-amber text-track-amber-ink',
	blue: 'bg-blue-10 text-cornflower-blue',
	orange: 'bg-texas-rose-10 text-flush-orange',
	green: 'bg-track-teal text-white',
	violet: 'bg-primary-violet text-white',
	gray: 'bg-track-surface text-track-slate',
};

export const TRACKING_TABS: {
	key: TrackingTab;
	labelKey: string;
	defaultLabel: string;
}[] = [
	{
		key: 'general',
		labelKey: 'orderTracking.tabs.general',
		defaultLabel: 'General',
	},
	{
		key: 'documents',
		labelKey: 'orderTracking.tabs.documents',
		defaultLabel: 'Documents',
	},
];

export const TRACKING_DOCUMENT_GROUPS: {
	key: TrackingDocumentGroupKey;
	steps: TrackingStepKey[];
	labelKey: string;
	defaultLabel: string;
}[] = [
	{
		key: 'loading',
		steps: ['arrived_to_loading', 'loaded'],
		labelKey: 'orderTracking.proofOfLoading',
		defaultLabel: 'Proof of loading',
	},
	{
		key: 'delivery',
		steps: ['to_unloading', 'unloaded', 'completed'],
		labelKey: 'orderTracking.proofOfDelivery',
		defaultLabel: 'Proof of delivery',
	},
];

const humanize = (key: string): string => {
	const text = key
		.replace(/([A-Z])/g, ' $1')
		.toLowerCase()
		.trim();
	return text.charAt(0).toUpperCase() + text.slice(1);
};

export const buyerStepsFrom = (keys: string[]): TrackingBuyerStep[] =>
	keys.map((key) => ({
		labelKey: `orderStepper.${key}`,
		defaultLabel: humanize(key),
	}));

export const TRACKING_SHEET_COLLAPSED_VH = 38;
export const TRACKING_SHEET_EXPANDED_VH = 86;
